import React, { memo, useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import { ExportActionsTypes, FileTypeSelectionCommonForm, Modal, Table } from '@components/common';
import DescriptionModal from '@components/common/ModalComponents/DescriptionModal/DescriptionModal';

import { useExportKpiAlertsHistory } from '@hooks/kpiAlerts/useExportKpiAlertsHistory';
import { useKpiAlertsHistory } from '@hooks/kpiAlerts/useKpiAlertsHistory';

import { kpiAlertsHistory } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setHistoryFireTime,
	setHistoryPage,
	setHistoryPageSize,
	setHistorySearchValue,
	setHistorySeverity,
	setHistorySort,
	setHistoryWasSent,
} from '@slices/kpiAlerts';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IHandlePreparingExportParams } from '@components/common/FileTypeSelectionForm/FileTypeSelectionForm.types';

import { TVendorsValues } from '@enums/common';
import { TSeverity } from '@enums/kpi-common';

import { exportRadioButtonsData } from '@constants/common';

import { getColumns, getDateFilters, getFilters } from './utils';

interface IKpiAlertsHistoryTableProps {
	selectedVendor: TSelectOption<TVendorsValues>;
}

function KpiAlertsHistoryTable({
	selectedVendor,
}: IKpiAlertsHistoryTableProps) {
	const dispatch = useAppDispatch();
	const [notes, setNotes] = useState<string | null>(null);

	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [exportingConfig, setExportingConfig] = useState<{ ids: string[]; actionType: string } | null>(null);

	const openExportModal = useCallback((ids: string[], actionType: string) => {
		setExportingConfig({ ids, actionType });

		setIsExportModalOpen(true);
	}, []);

	const closeExportModal = useCallback(() => {
		setIsExportModalOpen(false);
	}, []);

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		severity,
		fireTime,
		wasSent,
	} = useAppSelector(kpiAlertsHistory);

	const {
		kpiAlertsHistory: history,
		countOfRecords,
		severities,
		isLoading,
	} = useKpiAlertsHistory({
		page: page as number,
		pageSize: pageSize as number,
		searchValue,
		sortFieldName: sortFieldName ? sortFieldName : undefined,
		sortDirection: typeof sortDirection === 'boolean' ? sortDirection : null,
		severity: severity as string,
		isSent: wasSent as boolean,
		from: fireTime || undefined,
		vendor: selectedVendor?.value || null,
	});

	const {
		isLoading: isExporting,
		exportKpiAlertsHistory,
	} = useExportKpiAlertsHistory({
		filter: {
			searchValue,
			severity: severity ? severity : undefined,
			isActive: typeof wasSent === 'boolean' ? wasSent : undefined,
			from: fireTime || undefined,
		},
		ids: exportingConfig?.ids,
		isAll: exportingConfig
			? exportingConfig.actionType === ExportActionsTypes.all
			|| exportingConfig.actionType === ExportActionsTypes.allFiltered
			: false,
		onSuccess: () => setExportingConfig(null),
		vendor: selectedVendor?.value,
	});

	const openNotesModal = useCallback((value: string) => {
		setNotes(value);
	}, []);

	const columns: ColumnDef<any>[] = useMemo(() => {
		return getColumns({
			openNotesModal,
		});
	}, [openNotesModal]);

	const onSearchHandler = useCallback(
		(searchValue: string) => dispatch(setHistorySearchValue({ searchValue })),
		[dispatch],
	);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			placeholder: 'Search text',
		},
	], [onSearchHandler, searchValue]);

	const pageHandler = useCallback((page: number) => dispatch(setHistoryPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback(
		(pageSize: number) => dispatch(setHistoryPageSize({ pageSize })),
		[dispatch]);

	const pagination: ITablePagination = useMemo(() => {
		return {
			countOfRecords: countOfRecords || 0,
			forcePage: page ? page : undefined,
			size: pageSize ? pageSize : undefined,
			pageHandler,
			pageSizeHandler,
		};
	}, [countOfRecords, page, pageHandler, pageSize, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setHistorySort({
			sortDirection: sortBy[0] ? !!sortBy[0]?.desc : null,
			sortFieldName: sortBy[0] ? sortBy[0]?.id : null,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const handleSetSelectedSeverity = useCallback((severity: TSelectOption<TSeverity>): void => {
		dispatch(setHistorySeverity({ severity: severity?.value ? severity.value : null }));
	}, [dispatch]);

	const handleSetFireTime = useCallback((value: number | null): void => {
		dispatch(setHistoryFireTime({ fireTime: value }));
	}, [dispatch]);

	const handleSetWasSent = useCallback((wasSent: TSelectOption<boolean>): void => {
		dispatch(setHistoryWasSent({ wasSent: wasSent === null ? null : wasSent.value! }));
	}, [dispatch]);

	const filters: TTableFilter[] = useMemo(() => {
		return getFilters({
			severity,
			wasSent: wasSent === null
				? null : {
					label: wasSent ? 'Yes' : 'No',
					value: wasSent,
				} as TSelectOption<boolean>,
			handleSetSelectedSeverity,
			handleSetWasSent,
			severityOptions: severities,
		});
	}, [
		severity,
		severities,
		wasSent,
		handleSetSelectedSeverity,
		handleSetWasSent,
	]);

	const dateFilters = useMemo(() => {
		return getDateFilters({
			fireDate: fireTime ? new Date(fireTime) : null,
			onChangeFireDate: handleSetFireTime,
		});
	}, [fireTime, handleSetFireTime]);

	const onCloseDescriptionModal = (): void => {
		setNotes(null);
	};

	const handleExportKpiAlerts = useCallback(({
		fileType,
	}: IHandlePreparingExportParams): void => {
		exportKpiAlertsHistory(fileType);
	}, [exportKpiAlertsHistory]);

	return (
		<>
			<Table
				columns={columns}
				data={history || []}
				dateFilters={dateFilters}
				exportTooltipText='Export KPI alerts history'
				filters={filters}
				handleExport={openExportModal}
				isExport
				isExportSettings={false}
				isLoading={!!isLoading || isExporting}
				isOpenSearch={!!searchValue?.length}
				isWithoutActions
				isWithoutDeleteAction
				isWithoutDeleteActionMenu
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
			{notes && (
				<Modal
					isOpen={!!notes}
					onClose={onCloseDescriptionModal}
					title='Description'
				>
					<DescriptionModal
						description={notes}
						onClose={onCloseDescriptionModal}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isExportModalOpen}
				onClose={closeExportModal}
				title='File extension to export'
			>
				<FileTypeSelectionCommonForm
					closeModals={closeExportModal}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={handleExportKpiAlerts}
					isLoading={isExporting}
					toggleModal={closeExportModal}
				/>
			</Modal>
		</>
	);
}

export default memo(KpiAlertsHistoryTable);
